export type SockProfile = 'invisible' | 'low-cut' | 'ankle' | 'crew' | 'knee-high'

export type SockContourPaths = {
  base: string
  cuff: string
  legFront: string
  legBack: string
  ankle: string
  heel: string
  instep: string
  sole: string
  toe: string
}

export type SockContourGeometry = {
  profile: SockProfile
  labelProfile: SockProfile
  viewBox: string
  legTop: number
  cuffBottom: number
  legDimensionX: number
  legDimensionBottom: number
  hasLegZones: boolean
  hasLegDimension: boolean
  decorationAnchors: Record<string, SockDecorationAnchor>
  cuffDecorationAnchor: SockDecorationAnchor
  paths: SockContourPaths
}

export type SockDecorationAnchor = {
  x: number
  y: number
  width: number
  bandHeight: number
  rotation?: number
}

const profileGeometry: Record<SockProfile, { top: number; cuff: number; left: number; right: number }> = {
  invisible: { top: 206, cuff: 222, left: 54, right: 113 },
  'low-cut': { top: 158, cuff: 181, left: 42, right: 124 },
  ankle: { top: 108, cuff: 134, left: 40, right: 125 },
  crew: { top: 42, cuff: 72, left: 38, right: 126 },
  'knee-high': { top: 8, cuff: 40, left: 32, right: 132 },
}

export const sockContourLabelPositions: Record<SockProfile, Record<string, { x: number; y: number }>> = {
  'knee-high': {
    top_cuff: { x: 62, y: 23 },
    leg_front: { x: 91, y: 88 },
    leg_back: { x: 25, y: 128 },
    ankle_area: { x: 73, y: 194 },
    heel_area: { x: 58, y: 252 },
    instep_area: { x: 146, y: 230 },
    sole_area: { x: 174, y: 289 },
    toe_area: { x: 258, y: 260 },
  },
  crew: {
    top_cuff: { x: 62, y: 57 },
    leg_front: { x: 91, y: 91 },
    leg_back: { x: 20, y: 128 },
    ankle_area: { x: 73, y: 194 },
    heel_area: { x: 58, y: 252 },
    instep_area: { x: 146, y: 230 },
    sole_area: { x: 174, y: 289 },
    toe_area: { x: 258, y: 260 },
  },
  ankle: {
    top_cuff: { x: 61, y: 121 },
    leg_front: { x: 91, y: 143 },
    leg_back: { x: 18, y: 168 },
    ankle_area: { x: 73, y: 202 },
    heel_area: { x: 58, y: 252 },
    instep_area: { x: 146, y: 230 },
    sole_area: { x: 174, y: 289 },
    toe_area: { x: 258, y: 260 },
  },
  'low-cut': {
    top_cuff: { x: 61, y: 169 },
    ankle_area: { x: 86, y: 201 },
    heel_area: { x: 58, y: 252 },
    instep_area: { x: 146, y: 224 },
    sole_area: { x: 174, y: 286 },
    toe_area: { x: 258, y: 258 },
  },
  invisible: {
    top_cuff: { x: 72, y: 210 },
    ankle_area: { x: 75, y: 233 },
    heel_area: { x: 45, y: 268 },
    instep_area: { x: 156, y: 238 },
    sole_area: { x: 180, y: 296 },
    toe_area: { x: 280, y: 266 },
  },
}

export function resolveSockProfile(sockType?: string, legHeight?: number | null): SockProfile {
  const height = Number(legHeight ?? 0)
  if (sockType === 'sledi') return 'invisible'
  if (height > 0 && height <= 60) return 'low-cut'
  if (sockType === 'kalta' || (height > 60 && height <= 130)) return 'ankle'
  if (height >= 250) return 'knee-high'
  return 'crew'
}

export function createSockContourGeometry(sockType?: string, legHeight?: number | null): SockContourGeometry {
  const profile = resolveSockProfile(sockType, legHeight)
  const geometry = profileGeometry[profile]
  const rearStraightEnd = Math.max(168, geometry.cuff + 5)
  const invisible = profile === 'invisible'
  const calibratedNoShow = sockType === 'sledi' && invisible
  const calibratedShort = sockType === 'kalta'
  const calibratedLong = sockType === 'uzun' && ['crew', 'knee-high'].includes(profile)
  const viewBox = calibratedLong
    ? '0 0 380 340'
    : calibratedShort
      ? '0 82 380 258'
    : profile === 'invisible'
    ? '0 190 380 150'
    : profile === 'low-cut'
      ? '0 140 380 200'
      : profile === 'ankle'
        ? '0 82 380 258'
        : profile === 'crew'
          ? '0 25 380 315'
          : '0 0 380 340'
  // Primary perimeter from frontend/sock-no-show.svg (source bounds x=390..942,
  // y=357..962), rotated into a horizontal manufacturing side profile.
  const noShowPaths: SockContourPaths = {
    base: 'M25 195C25 214 25 231 25 242C34 255 48 266 62 272C136 287 218 304 296 320C320 319 345 307 355 293C360 280 360 268 355 259C342 246 325 236 305 230C268 216 231 205 193 197L127 225C88 218 53 205 25 195Z',
    cuff: 'M25 195C54 205 89 218 127 225L193 197L181 213L127 242C88 231 52 217 27 208Z',
    legFront: '',
    legBack: '',
    ankle: 'M27 208C52 217 88 231 127 242L181 213L193 197C177 213 160 230 143 247C101 240 62 226 27 208Z',
    heel: 'M27 208C25 219 24 232 25 242C34 255 48 266 62 272L108 281C103 264 109 251 127 242C88 231 52 217 27 208Z',
    instep: 'M181 213L193 197C231 205 268 216 305 230C322 236 337 245 347 254L310 260L108 281C103 264 109 251 127 242Z',
    sole: 'M108 281L310 260L330 304C319 313 307 318 296 320C218 304 136 287 62 272Z',
    toe: 'M305 230C325 236 342 246 355 259C360 268 360 280 355 293C349 298 340 302 330 304L310 260L347 254C336 244 322 236 305 230Z',
  }
  // Outer contour reconstructed from the major traced strokes in frontend/sock-short.svg
  // (source bounds x=291..949, y=105..1039), mirrored into the standard toe-right view.
  const shortPaths: SockContourPaths = {
    base: 'M49 108H193V166C193 176 197 184 204 191L341 268C350 270 355 274 355 278C355 294 348 302 341 305C326 315 310 319 295 319C277 320 259 318 244 314L106 252C70 235 42 220 22 203C18 190 25 177 35 167C44 157 49 145 49 133V108Z',
    cuff: 'M49 108H193V134H49Z',
    legFront: 'M120 134H193V166C193 176 197 184 204 191L170 211C151 195 138 181 130 166C124 154 120 144 120 134Z',
    legBack: 'M49 134H120C120 151 116 166 107 179C95 197 78 214 56 229C40 220 29 212 22 203C18 190 25 177 35 167C44 157 49 145 49 133Z',
    ankle: 'M56 229C78 214 95 197 107 179C114 169 118 159 120 149C124 166 133 182 147 196C155 203 163 208 170 211L151 230C116 233 84 232 56 229Z',
    heel: 'M56 229C42 220 29 212 22 203C18 190 25 177 35 167C44 157 49 145 49 133C58 161 74 190 96 218C111 236 126 251 141 265L106 252C86 243 69 235 56 229Z',
    instep: 'M170 211L204 191L341 268C346 269 350 272 353 275L298 281L141 265C150 246 160 228 170 211Z',
    sole: 'M141 265L298 281L320 315C311 318 303 319 295 319C277 320 259 318 244 314L106 252Z',
    toe: 'M341 268C350 270 355 274 355 278C355 294 348 302 341 305C334 310 327 313 320 315L298 281L353 275C350 272 346 269 341 268Z',
  }
  // Main silhouette extracted from frontend/sock-long.svg (source viewBox 0 0 1364 1153,
  // outline bounds x=388..887, y=61..1085), mirrored and calibrated to 380 x 340.
  const longPaths: SockContourPaths = {
    base: 'M43 8H183C186 45 186 87 182 122C181 143 185 160 190 178C214 198 248 216 292 236C326 252 350 267 354 280C358 294 345 307 316 315C285 323 253 320 234 313C199 296 164 277 131 258C96 239 60 229 34 216C17 207 16 190 25 178C35 165 46 154 49 143C52 126 49 105 47 83C44 57 43 33 43 8Z',
    cuff: 'M43 8H183V40H43Z',
    legFront: 'M113 40H183C185 74 185 103 182 122C181 143 185 160 190 178L162 199C145 184 132 169 124 151C116 133 113 111 113 86Z',
    legBack: 'M43 40H113V86C113 111 110 133 101 151C90 171 72 190 49 207L34 216C22 209 18 200 20 191C21 186 23 182 25 178C35 165 46 154 49 143C52 126 49 105 47 83C44 63 43 49 43 40Z',
    ankle: 'M49 207C72 190 90 171 101 151C107 141 111 129 113 116C116 138 122 155 132 169C141 182 151 192 162 199L146 222C113 220 81 216 49 207Z',
    heel: 'M49 207C38 215 33 217 34 216C17 207 16 190 25 178C35 165 46 154 49 143C57 163 69 183 86 202C101 219 116 237 131 258C96 239 60 229 34 216Z',
    instep: 'M162 199L190 178C214 198 248 216 292 236C309 244 322 251 332 258L294 272L131 258C142 238 152 218 162 199Z',
    sole: 'M131 258L294 272L316 315C285 323 253 320 234 313C199 296 164 277 131 258Z',
    toe: 'M292 236C326 252 350 267 354 280C358 294 345 307 316 315L294 272L332 258C318 249 305 242 292 236Z',
  }

  const cuffDecorationAnchor = calibratedLong
    ? { x: 43, y: 8, width: 140, bandHeight: 32 }
    : calibratedShort
      ? { x: 49, y: 108, width: 144, bandHeight: 26 }
      : calibratedNoShow
        ? { x: 39, y: 199, width: 142, bandHeight: 24 }
        : { x: geometry.left, y: geometry.top, width: geometry.right - geometry.left, bandHeight: geometry.cuff - geometry.top }
  const decorationAnchors: Record<string, SockDecorationAnchor> = calibratedLong
    ? {
      top_cuff: cuffDecorationAnchor,
      leg_front: { x: 125, y: 55, width: 112, bandHeight: 26, rotation: 90 },
      leg_back: { x: 42, y: 63, width: 125, bandHeight: 25, rotation: 90 },
      ankle_area: { x: 64, y: 202, width: 92, bandHeight: 18, rotation: 8 },
      heel_area: { x: 42, y: 224, width: 76, bandHeight: 24, rotation: 18 },
      instep_area: { x: 151, y: 210, width: 156, bandHeight: 24, rotation: 8 },
      sole_area: { x: 132, y: 284, width: 160, bandHeight: 22, rotation: 8 },
      toe_area: { x: 291, y: 253, width: 50, bandHeight: 30, rotation: 24 },
    }
    : calibratedShort
      ? {
        top_cuff: cuffDecorationAnchor,
        leg_front: { x: 124, y: 138, width: 72, bandHeight: 24, rotation: 88 },
        leg_back: { x: 51, y: 143, width: 80, bandHeight: 24, rotation: 90 },
        ankle_area: { x: 59, y: 219, width: 86, bandHeight: 18, rotation: 8 },
        heel_area: { x: 44, y: 220, width: 78, bandHeight: 25, rotation: 20 },
        instep_area: { x: 156, y: 223, width: 150, bandHeight: 22, rotation: 8 },
        sole_area: { x: 142, y: 287, width: 150, bandHeight: 20, rotation: 10 },
        toe_area: { x: 309, y: 263, width: 46, bandHeight: 26, rotation: 22 },
      }
      : calibratedNoShow
        ? {
          top_cuff: cuffDecorationAnchor,
          ankle_area: { x: 57, y: 222, width: 88, bandHeight: 16, rotation: 12 },
          heel_area: { x: 36, y: 235, width: 78, bandHeight: 24, rotation: 20 },
          instep_area: { x: 132, y: 221, width: 166, bandHeight: 22, rotation: 8 },
          sole_area: { x: 124, y: 286, width: 174, bandHeight: 20, rotation: 7 },
          toe_area: { x: 310, y: 246, width: 44, bandHeight: 26, rotation: 22 },
        }
        : {
          top_cuff: cuffDecorationAnchor,
          leg_front: { x: 86, y: geometry.cuff + 8, width: Math.max(55, 176 - geometry.cuff), bandHeight: 24, rotation: 90 },
          leg_back: { x: geometry.left + 4, y: geometry.cuff + 8, width: Math.max(48, rearStraightEnd - geometry.cuff), bandHeight: 24, rotation: 90 },
          ankle_area: { x: 52, y: 210, width: 96, bandHeight: 17, rotation: 8 },
          heel_area: { x: 38, y: 229, width: 78, bandHeight: 24, rotation: 16 },
          instep_area: { x: 132, y: 223, width: 164, bandHeight: 22, rotation: 7 },
          sole_area: { x: 118, y: 286, width: 174, bandHeight: 20, rotation: 6 },
          toe_area: { x: 298, y: 252, width: 50, bandHeight: 28, rotation: 22 },
        }

  return {
    profile,
    labelProfile: calibratedLong ? 'knee-high' : calibratedShort ? 'ankle' : profile,
    viewBox,
    legTop: calibratedLong ? 8 : calibratedShort ? 108 : geometry.top,
    cuffBottom: calibratedLong ? 40 : calibratedShort ? 134 : geometry.cuff,
    legDimensionX: calibratedLong ? 200 : calibratedShort ? 215 : 139,
    legDimensionBottom: calibratedLong ? 178 : calibratedShort ? 191 : 218,
    hasLegZones: calibratedShort || ['ankle', 'crew', 'knee-high'].includes(profile),
    hasLegDimension: profile !== 'invisible',
    decorationAnchors,
    cuffDecorationAnchor,
    paths: calibratedLong ? longPaths : calibratedShort ? shortPaths : calibratedNoShow ? noShowPaths : {
      base: invisible
        ? 'M55 214C73 201 95 201 116 207C128 218 142 225 158 230C187 239 223 240 260 237C303 234 337 244 353 266C365 283 359 298 338 307C311 319 261 320 214 320H93C60 320 42 304 42 278C42 252 48 232 55 214Z'
        : `M${geometry.left} ${geometry.top}H${geometry.right}V181c0 22 11 36 34 45 28 10 68 13 108 11 44-2 75 8 88 28 11 17 4 29-15 36-24 11-75 9-121 9H93C58 310 34 300 22 280C10 260 15 235 32 216C43 203 ${geometry.left} 188 ${geometry.left} ${rearStraightEnd}V${geometry.top}Z`,
      cuff: invisible
        ? 'M55 214C73 201 95 201 116 207C121 211 125 216 128 220C102 215 78 216 58 226C54 223 53 219 55 214Z'
        : `M${geometry.left} ${geometry.top}H${geometry.right}V${geometry.cuff}H${geometry.left}Z`,
      legFront: `M82 ${geometry.cuff + 2}H${geometry.right}V181c0 18 7 31 19 40l-36-3c-10-11-15-23-15-39V${geometry.cuff + 2}Z`,
      legBack: `M${geometry.left} ${geometry.cuff + 2}H82V166C82 186 77 199 66 209C54 211 43 214 32 216C43 203 ${geometry.left} 188 ${geometry.left} ${rearStraightEnd}V${geometry.cuff + 2}Z`,
      ankle: invisible
        ? 'M58 226C78 216 102 215 128 220L145 229C117 225 90 227 65 234Z'
        : 'M32 216C43 214 54 211 66 209C88 215 112 219 136 219c5 3 10 6 14 7l-22 10c-34-2-66-9-96-20Z',
      heel: invisible
        ? 'M58 226C47 240 42 257 42 278C42 304 60 320 93 320H137L123 278C121 255 128 238 145 229C113 224 82 223 58 226Z'
        : 'M32 216C15 235 10 260 22 280C34 300 58 310 93 310H121L108 272C106 253 112 237 128 226C92 224 58 220 32 216Z',
      instep: invisible
        ? 'M145 229C178 238 218 240 260 237C282 236 302 239 318 246L294 278H123C121 255 128 238 145 229Z'
        : 'M128 226c36 10 85 13 130 11 22-1 41 1 56 7l-20 28H108C106 253 112 237 128 226Z',
      sole: invisible ? 'M123 278H294L319 316H137Z' : 'M108 272h186l23 38H121Z',
      toe: invisible
        ? 'M260 237C303 234 337 244 353 266C365 283 359 298 338 307C332 311 325 313 319 316L294 278Z'
        : 'M258 237c44-2 75 8 88 28 11 17 4 29-15 36-4 2-9 4-14 5l-23-33Z',
    },
  }
}
